import React from 'react';
import { ChevronRight, X } from 'lucide-react';

/**
 * FirstTimeTip — inline, dismissible speech-bubble that hovers above
 * a metric card the very first time a user lands on the dashboard.
 *
 * The dashboard owns the tour state (which step is active) and tells
 * each card whether to render its tip. Each tip writes its own
 * dismissal flag to localStorage and then calls onAdvance() so the
 * next tip in the sequence appears on the next card.
 *
 * The content is intentionally TIGHTER than the glossary modal —
 * one or two sentences max. Users who want the full definition can
 * tap the (i) icon any time. This is just the orientation pass.
 */
export const TIP_CONTENT = {
  pe: {
    title: 'Personal Effectiveness',
    body: 'How effective you are in your role, right now. Capability × Motivation. Most people land between 50% and 80%.',
  },
  capability: {
    title: 'Capability',
    body: 'Whether you have the skills your role needs — averaged across all of them, weighted against the level your role asks for.',
  },
  motivation: {
    title: 'Motivation',
    body: 'How much you want to do the work — and how supported you feel doing it. Made of intrinsic, extrinsic, and manager support signals.',
  },
  lei: {
    title: 'Personal Learning Effectiveness Index',
    body: 'Your learning quality score out of 100. Are the things you learn actually changing what you do at work?',
  },
};

const TOUR_ORDER = ['pe', 'capability', 'motivation', 'lei'];

const STORAGE_PREFIX = 'pie.tour.';

export function getInitialTourStage() {
  if (typeof window === 'undefined') return null;
  for (const key of TOUR_ORDER) {
    if (!window.localStorage.getItem(STORAGE_PREFIX + key)) return key;
  }
  return null;
}

export function dismissAllTours() {
  TOUR_ORDER.forEach((k) => window.localStorage.setItem(STORAGE_PREFIX + k, '1'));
}

export function dismissTour(key) {
  window.localStorage.setItem(STORAGE_PREFIX + key, '1');
}

export function nextTourStage(current) {
  const i = TOUR_ORDER.indexOf(current);
  return TOUR_ORDER[i + 1] || null;
}

export default function FirstTimeTip({ tipKey, isLast, onAdvance, onSkipAll, position = 'below' }) {
  const content = TIP_CONTENT[tipKey];
  if (!content) return null;
  return (
    <div
      className={`pie-firsttip pie-firsttip-${position}`}
      role="dialog"
      aria-labelledby={`pie-firsttip-${tipKey}-title`}
      data-testid={`pie-firsttip-${tipKey}`}
    >
      <button
        type="button"
        className="pie-firsttip-close"
        onClick={onSkipAll}
        aria-label="Skip all intro tips"
        title="Skip the rest of these intro tips"
        data-testid={`pie-firsttip-${tipKey}-skip`}
      >
        <X size={11} />
      </button>
      <div className="pie-firsttip-eyebrow">A quick what&apos;s this?</div>
      <h4 id={`pie-firsttip-${tipKey}-title`} className="pie-firsttip-title">
        {content.title}
      </h4>
      <p className="pie-firsttip-body">{content.body}</p>
      <div className="pie-firsttip-foot">
        <button
          type="button"
          className="pie-firsttip-cta"
          onClick={onAdvance}
          data-testid={`pie-firsttip-${tipKey}-cta`}
        >
          {isLast ? 'Got it' : <>Next <ChevronRight size={11} /></>}
        </button>
      </div>
    </div>
  );
}

export const TOUR_KEYS = TOUR_ORDER;
